import React, { useState, useEffect } from "react";
import { AppBar, Toolbar, Box, Grid, Typography, Tooltip, TableContainer, Divider, Table, TableBody, TableHead, TableCell, TableRow, IconButton, TextField, Autocomplete, useTheme, alpha, Button, Menu, MenuItem, ListItemIcon, ListItemText, CssBaseline, Stack, Paper } from "@mui/material";

import FileDownloadIcon from '@mui/icons-material/FileDownload';
import Close from '@mui/icons-material/Close';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import HelpIcon from '@mui/icons-material/HelpOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SearchIcon from '@mui/icons-material/Search';

import { tokens } from "../../../theme/theme";
import { callApi } from "../../../utils/apiClient";
import { Production_API_ENDPOINTS } from "../../../config/api/apiCallProduction";
import SkeletonTableLoader from "../../../components/table/SkeletonTableLoader";
import CustomTableContainer from "../../../components/table/CustomTableContainer";
import TableFilterRow from "../../../components/table/TableFilterRow";
import CustomPagination from "../../../components/table/CustomPagination";
import ResponsiveBox from "../../../components/form/ResponsiveScrollBox";
import { exportToExcel } from "../../../components/ExportToExcel";
const StockStackingReport = ({ handleBackWinderMachineEntry }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [loading, setLoading] = useState(false);
    const [loadingData, setLoadingData] = useState([]);
    const [location, setLocation] = useState(null);
    const [locationOptions, setLocationOptions] = useState([]);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [recordsPerPage, setRecordsPerPage] = useState(20);
    const [currentPage, setCurrentPage] = useState(1);

    // Menu state
    const [anchorEl, setAnchorEl] = useState(null);
    const openMenu = Boolean(anchorEl);


    useEffect(() => {
        callApi(Production_API_ENDPOINTS.reports.REPORT_getscanlocations, 'POST', { 'Content-Type': 'application/json' }, { p_company_id: localStorage.getItem('companyid') })
            .then(data => {
                setLocationOptions(data.map(loc => ({ label: loc, value: loc })));
            })
            .catch(error => console.error('Error fetching scan locations:', error));
    }, []);

    const getReportData = () => {

        let parameters = {
            p_company_id: localStorage.getItem('companyid'),
            p_location: location?.value || null,
            p_from_date: fromDate || null,
            p_to_date: toDate || null,
        };
        setLoading(true);
        callApi(Production_API_ENDPOINTS.reports.REPORT_GetStockStackingReport, 'POST', { 'Content-Type': 'application/json' }, parameters)
            .then(data => {
                setLoadingData(data);
                setLoading(false);
            })
            .catch(error => {
                console.error('Error fetching stock stacking report:', error);
                alert(error);
            });

    };

    useEffect(() => {
        getReportData();
    }, [location]);

    // Menu handlers
    const handleMenuClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleMenuItemClick = (option) => {
        if (option === 'Export Report') {
            handleExportToExcel();
        } else if (option === 'Refresh List') {
            getReportData();
        }
        handleMenuClose();
    };

    const [filters, setFilters] = useState({
        item_no: '',
        product_type: '',
        process_stage: '',
        order_no: '',
        quality: '',
        gsm: '',
        location: '',
    });
    const filteredData = loadingData.filter((row) => {
        return Object.keys(filters).every((key) => {
            if (filters[key] === '') return true;
            return String(row[key] || '').toLowerCase().includes(filters[key].toLowerCase());
        });
    });

    const excelColumns = [
        { header: "PRODUCT CODE", key: "item_no" },
        { header: "PRODUCT TYPE", key: "product_type" },
        { header: "PROCESS STAGE", key: "process_stage" },
        { header: "ORDER NO", key: "order_no" },
        { header: "QUALITY", key: "quality" },
        { header: "GSM", key: "gsm" },
        { header: "WIDTH", key: "width" },
        { header: "LENGTH", key: "length" },
        { header: "WEIGHT (KG)", key: "weight" },
        { header: "LOCATION", key: "location" },
        { header: "SHEETS IN REAMS", key: "sheets_in_reams" },
        { header: "REAM WEIGHT", key: "ream_weight" },
        { header: "REEL OD", key: "reel_od" },
        { header: "CORE ID", key: "core_id" },
        { header: "SCANNED DATETIME", key: "scanned_datetime" },
        { header: "SCANNED BY", key: "scanned_by" },
    ];

    const handleExportToExcel = () => {
        exportToExcel({
            data: filteredData,
            fileName: 'Stock Stacking Report.xlsx',
            sheetName: 'Stock Stacking',
            columns: excelColumns,
            dateColumnIndexes: [],
        });
    };

    const handleFilterChange = (e, column) => {
        setFilters({ ...filters, [column]: e.target.value });
    };

    const handlePageChange = (event, value) => {
        setCurrentPage(value);
    };

    const pageCount = Math.ceil(filteredData.length / recordsPerPage);

    const uniqueValues = {
        item_no: [...new Set(loadingData.map(row => row.item_no))],
        product_type: [...new Set(loadingData.map(row => row.product_type))],
        process_stage: [...new Set(loadingData.map(row => row.process_stage))],
        order_no: [...new Set(loadingData.map(row => row.order_no))],
        quality: [...new Set(loadingData.map(row => row.quality))],
        gsm: [...new Set(loadingData.map(row => row.gsm))],
        location: [...new Set(loadingData.map(row => row.location))],
        scanned_by: [...new Set(loadingData.map(row => row.scanned_by))],
    };



    // Get the records for the current page
    const currentRecords = filteredData.slice(
        (currentPage - 1) * recordsPerPage,
        currentPage * recordsPerPage
    );


    const columns = ['item_no', 'product_type', 'scanned_datetime', 'scanned_by', 'process_stage', 'order_no', 'quality', 'gsm', 'width', 'length', 'weight', 'location', 'sheets_in_reams', 'ream_weight', 'reel_od', 'core_id'];

    const autoCompleteColumns = ['item_no', 'product_type', 'process_stage', 'order_no', 'quality', 'gsm', 'location', 'scanned_by'];

    const totals = {
        weight: filteredData.reduce((sum, r) => sum + (parseFloat(r.weight) || 0), 0),
        sheets_in_reams: filteredData.reduce((sum, r) => sum + (parseInt(r.sheets_in_reams) || 0), 0),
        ream_weight: filteredData.reduce((sum, r) => sum + (parseFloat(r.ream_weight) || 0), 0),
    };




    return (
        <>
            <CssBaseline />
            <AppBar position="static" sx={{ backgroundColor: "inherit", borderRadius: "0px", height: "48px", justifyContent: "center" }}>
                <Toolbar>
                    <Button
                        color="secondary"
                        sx={{ textTransform: 'none', fontSize: "16px", fontWeight: "500" }}
                    >
                        Stock Stacking Report
                    </Button>

                    {/* More Options Menu */}
                    <IconButton
                        size="small"
                        sx={{ marginLeft: 'auto' }}
                        onClick={handleMenuClick}
                    >
                        <MoreVertIcon sx={{ fontSize: "18px" }} />
                    </IconButton>
                    <Menu
                        anchorEl={anchorEl}
                        open={openMenu}
                        onClose={handleMenuClose}
                    >
                        <MenuItem onClick={() => handleMenuItemClick('Export Report')}>
                            <ListItemIcon>
                                <FileDownloadIcon />
                            </ListItemIcon>
                            <ListItemText primary="Export Report" />
                        </MenuItem>
                        <Divider />
                        <MenuItem onClick={() => handleMenuItemClick('Refresh List')}>
                            <ListItemIcon>
                                <MoreVertIcon />
                            </ListItemIcon>
                            <ListItemText primary="Refresh List" />
                        </MenuItem>
                    </Menu>

                    <IconButton
                        size="small"
                        sx={{ mr: 1, ml: 1 }}
                    >
                        <HelpIcon sx={{ fontSize: "18px" }} />
                    </IconButton>

                    <IconButton onClick={handleBackWinderMachineEntry}>
                        <Close fontSize="inherit" style={{ fontSize: "18px", color: "#2196F3" }} />
                    </IconButton>
                </Toolbar>
            </AppBar>

            <Paper
                elevation={0}
                sx={{
                    backgroundColor: alpha(theme.palette.secondary.main, 0.08),
                    p: 1.5,
                    mb: 1,
                    ml: 2,
                    mr: 2,
                    mt: 0.5,
                    borderRadius: 1.5,
                    border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`
                }}
            >
                <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                    <LocationOnIcon sx={{ color: theme.palette.secondary.main, fontSize: 20 }} />
                    <Autocomplete
                        size="small"
                        options={locationOptions}
                        value={location}
                        onChange={(event, newValue) => setLocation(newValue)}
                        getOptionLabel={(option) => option?.label || ''}
                        isOptionEqualToValue={(option, value) => option?.value === value?.value}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Filter by Location"
                                placeholder="Select a location..."
                                variant="outlined"
                            />
                        )}
                        sx={{
                            minWidth: 280,
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: theme.palette.background.default,
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: theme.palette.secondary.main,
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: theme.palette.secondary.main,
                                },
                            },
                            '& .MuiInputLabel-root.Mui-focused': {
                                color: theme.palette.secondary.main,
                            },
                        }}
                        autoFocus={true}
                        clearOnBlur
                        noOptionsText="No locations available"
                    />
                    <TextField
                        label="From Date"
                        type="date"
                        size="small"
                        color="secondary"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{
                            minWidth: 160,
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: theme.palette.background.default,
                            },
                        }}
                    />
                    <TextField
                        label="To Date"
                        type="date"
                        size="small"
                        color="secondary"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{
                            minWidth: 160,
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: theme.palette.background.default,
                            },
                        }}
                    />
                    <Button
                        variant="contained"
                        color="secondary"
                        size="small"
                        onClick={getReportData}
                        startIcon={<SearchIcon />}
                        sx={{ textTransform: 'none', height: 40, minWidth: 100, fontSize: '0.85rem' }}
                    >
                        Search
                    </Button>
                </Stack>
            </Paper>

            <Divider />
            <ResponsiveBox colors={colors} customMaxHeight={{ xs: '72vh', sm: '68vh', md: '64vh', lg: '64vh', xl: '82vh' }}>

                <CustomTableContainer colors={colors}>

                    <TableContainer >
                        {loading ? (
                            <SkeletonTableLoader rows={5} columns={11} />
                        ) : (
                            <Table stickyHeader size="small" sx={{ tableLayout: 'fixed' }}>
                                <TableHead sx={{ height: 10 }}>
                                    <TableRow>
                                        
                                        <TableCell sx={{ width: '120px' }}>Product Code</TableCell>
                                        <TableCell sx={{ width: '120px' }}>Product Type</TableCell>
                                        <TableCell sx={{ width: '150px' }}>Scanned DateTime</TableCell>
                                        <TableCell sx={{ width: '160px' }}>Scanned By</TableCell>
                                        <TableCell sx={{ width: '120px' }}>Process Stage</TableCell>
                                        <TableCell sx={{ width: '100px' }}>Order No</TableCell>
                                        <TableCell sx={{ width: '100px' }}>Quality</TableCell>
                                        <TableCell sx={{ width: '80px' }}>GSM</TableCell>
                                        <TableCell sx={{ width: '80px' }}>Width</TableCell>
                                        <TableCell sx={{ width: '80px' }}>Length</TableCell>
                                        <TableCell sx={{ width: '100px', textAlign: 'right' }}>Weight (KG)</TableCell>
                                        <TableCell sx={{ width: '100px' }}>Location</TableCell>
                                        <TableCell sx={{ width: '120px', textAlign: 'right' }}>Sheets in Reams</TableCell>
                                        <TableCell sx={{ width: '100px', textAlign: 'right' }}>Ream Weight</TableCell>
                                        <TableCell sx={{ width: '90px', textAlign: 'right' }}>Reel OD</TableCell>
                                        <TableCell sx={{ width: '90px', textAlign: 'right' }}>Core ID</TableCell>
                                    </TableRow>

                                    <TableFilterRow
                                        columns={columns}
                                        filters={filters}
                                        handleFilterChange={handleFilterChange}
                                        uniqueValues={uniqueValues}
                                        autoCompleteColumns={autoCompleteColumns}
                                    />

                                </TableHead>
                                <TableBody>
                                    {currentRecords.map((row, index) => (
                                        <TableRow key={row.scan_id} sx={{
                                            backgroundColor:
                                                index % 2 === 0
                                                    ? theme.palette.background.default
                                                    : alpha(theme.palette.action.hover, 0.04),
                                        }}>
                                          
                                            <TableCell>{row.item_no}</TableCell>
                                            <TableCell>{row.product_type}</TableCell>
                                            <TableCell>{row.scanned_datetime ? new Date(row.scanned_datetime).toLocaleString() : ''}</TableCell>
                                            <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '160px' }}>
                                                <Tooltip title={row.scanned_by || ''} placement="top">
                                                    <span>{row.scanned_by}</span>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>{row.process_stage}</TableCell>
                                            <TableCell>{row.order_no}</TableCell>
                                            <TableCell>{row.quality}</TableCell>
                                            <TableCell sx={{ textAlign: 'right' }}>{row.gsm}</TableCell>
                                            <TableCell sx={{ textAlign: 'right' }}>{row.width}</TableCell>
                                            <TableCell sx={{ textAlign: 'right' }}>{row.length}</TableCell>
                                            <TableCell sx={{ textAlign: 'right' }}>{row.weight?.toFixed(2)}</TableCell>
                                            <TableCell>{row.location}</TableCell>
                                            <TableCell sx={{ textAlign: 'right' }}>{row.sheets_in_reams}</TableCell>
                                            <TableCell sx={{ textAlign: 'right' }}>{row.ream_weight?.toFixed(2)}</TableCell>
                                            <TableCell sx={{ textAlign: 'right' }}>{row.reel_od?.toFixed(2)}</TableCell>
                                            <TableCell sx={{ textAlign: 'right' }}>{row.core_id?.toFixed(2)}</TableCell>
                                        </TableRow>
                                    ))}

                                    <TableRow sx={{ backgroundColor: colors.primary[400], height: 10 }}>
                                        <TableCell colSpan={10} sx={{ fontWeight: 'bold', textAlign: 'right' }}>TOTAL</TableCell>
                                        <TableCell sx={{ textAlign: "right", fontWeight: 'bold' }}>{totals.weight.toFixed(2)}</TableCell>
                                        <TableCell />
                                        <TableCell sx={{ textAlign: "right", fontWeight: 'bold' }}>{totals.sheets_in_reams}</TableCell>
                                        <TableCell sx={{ textAlign: "right", fontWeight: 'bold' }}>{totals.ream_weight.toFixed(2)}</TableCell>
                                        <TableCell colSpan={2} />
                                    </TableRow>
                                </TableBody>


                            </Table>)}
                    </TableContainer>


                </CustomTableContainer>

            </ResponsiveBox>

            <CustomPagination pageCount={pageCount} currentPage={currentPage} onPageChange={handlePageChange} />

        </>
    )
};
export default StockStackingReport;